import { askConfirm } from '@/components/ui/ConfirmHost';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getExam, postExamAnswers, postExamStart, postExamSubmit, type ExamSession } from '@/api/applicant';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { CbtQuestion, Countdown, QuestionNav, fromAnswer, isAnswered, type CbtAnswer } from '@/components/ui/Cbt';
import { Banner } from '@/components/kit/ui';
import { errMsg, fmtDateTime } from '@/components/kit/data';
import { useAuth } from '@/stores/auth';
import { appKeys } from '../shared';
import { Actions, Body, Hero, Loading, Panel, Primary, Rows, Screen } from '../ui';

const draftKey = (examId: number) => `ojp-applicant-exam-${useAuth.getState().user?.id ?? 'anon'}-${examId}`;
const loadDraft = (examId: number): Record<number, CbtAnswer> => { try { return JSON.parse(localStorage.getItem(draftKey(examId)) ?? '{}'); } catch { return {}; } };
const saveDraft = (examId: number, a: Record<number, CbtAnswer> | null) => { try { if (a) localStorage.setItem(draftKey(examId), JSON.stringify(a)); else localStorage.removeItem(draftKey(examId)); } catch { /* storage unavailable */ } };

function Runner({ session, onDone }: { session: ExamSession; onDone: (r: { score: number | null; max_score: number | null }) => void }) {
  const [answers, setAnswers] = useState<Record<number, CbtAnswer>>(() => loadDraft(session.exam_id));
  const [i, setI] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const qs = session.questions;

  useEffect(() => { saveDraft(session.exam_id, answers); }, [answers, session.exam_id]);
  // Also save to the school's server every few seconds, so a dead phone or another device does not lose answers.
  useEffect(() => {
    const t = setTimeout(() => {
      const rows = qs.map((x) => ({ question_id: x.id, answer: fromAnswer(x, answers[x.id]) })).filter((x) => x.answer !== null);
      if (rows.length) postExamAnswers(rows).catch(() => { /* local draft still has them */ });
    }, 3000);
    return () => clearTimeout(t);
  }, [answers]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = useMutation({
    mutationFn: () => postExamSubmit(qs.map((q) => ({ question_id: q.id, answer: fromAnswer(q, answersRef.current[q.id]) })).filter((x) => x.answer !== null)),
    onSuccess: (r) => { saveDraft(session.exam_id, null); onDone(r); },
    onError: (e) => setErr(errMsg(e)),
  });
  const expire = useCallback(() => { if (!submit.isPending && !submit.isSuccess) submit.mutate(); }, [submit]);

  const done = qs.filter((q) => isAnswered(answers[q.id])).length;
  const q = qs[i];

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="font-semibold">{session.title}</h2>
          <p className="text-xs text-slate-500">{done} of {qs.length} answered · ends {fmtDateTime(session.deadline)}</p>
        </div>
        <Countdown deadline={session.deadline} onExpire={expire} />
      </div>
      {session.instructions && <p className="text-sm text-slate-600 mb-4 whitespace-pre-wrap">{session.instructions}</p>}
      {err && <div className="mb-3"><Banner>{err}</Banner></div>}
      {q ? (
        <>
          {q.subject && <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{q.subject}</p>}
          <CbtQuestion q={q} index={i} value={answers[q.id]} onChange={(v) => setAnswers((p) => ({ ...p, [q.id]: v }))} disabled={submit.isPending} />
          <div className="flex justify-between mt-4 gap-2">
            <Button variant="ghost" disabled={i === 0} onClick={() => setI(i - 1)}>Previous</Button>
            {i < qs.length - 1 ? <Button onClick={() => setI(i + 1)}>Next</Button> : (
              <Button variant="danger" disabled={submit.isPending} onClick={async () => { if (await askConfirm(done < qs.length ? `You have ${qs.length - done} unanswered question(s). Submit anyway?` : 'Submit your exam now?', { title: 'Submit exam', confirmLabel: 'Submit', destructive: false })) submit.mutate(); }}>{submit.isPending ? 'Submitting…' : 'Submit exam'}</Button>
            )}
          </div>
          <div className="mt-4 border-t pt-4"><QuestionNav count={qs.length} current={i} answered={(n) => isAnswered(answers[qs[n].id])} onPick={setI} /></div>
        </>
      ) : <p className="text-sm text-slate-500">This exam has no questions. Contact the school.</p>}
    </Card>
  );
}

export function ExamPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: appKeys.exam, queryFn: getExam });
  const [session, setSession] = useState<ExamSession | null>(null);
  const [result, setResult] = useState<{ score: number | null; max_score: number | null } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const exam = q.data?.exam ?? null;

  const start = useMutation({
    mutationFn: postExamStart,
    onSuccess: (s) => { setSession(s); setErr(null); },
    onError: (e) => setErr(errMsg(e)),
  });

  const finished = (r: { score: number | null; max_score: number | null }) => {
    setSession(null); setResult(r);
    for (const k of [appKeys.exam, appKeys.status, appKeys.result, appKeys.app]) qc.invalidateQueries({ queryKey: k });
  };

  if (q.isLoading) return <Screen><Hero title="Entrance exam" /><Loading /></Screen>;
  if (session) return <div className="max-w-3xl mx-auto"><Runner session={session} onDone={finished} /></div>;

  return (
    <Screen>
      <Hero title="Entrance exam" sub={exam ? (exam.status === 'scheduled' ? 'Booked' : exam.status === 'graded' || exam.status === 'taken' ? 'Done' : exam.status) : 'Not booked yet'} />
      <Body>
        {err && <Panel tone="red"><p className="text-sm text-red-700">{err}</p></Panel>}
        {result && <Panel tone="green"><p className="font-bold text-green-900">Exam submitted.</p>{result.score !== null && result.max_score ? <p className="text-sm text-green-800">You scored {result.score} out of {result.max_score}.</p> : null}</Panel>}
        {!exam ? (
          <Panel><p className="text-slate-600">Your entrance exam is booked automatically once your application fee is paid. We will notify you.</p></Panel>
        ) : (
          <>
            <Panel><Rows rows={[
              ['Exam', exam.name],
              ['Date', exam.scheduled_at ? fmtDateTime(exam.scheduled_at) : 'Open now'],
              ['Where', exam.mode === 'cbt' ? (exam.venue ? `${exam.venue} or online` : 'Online, on this device') : exam.venue ?? 'At the school'],
              ...(exam.duration_minutes ? [['Duration', `${exam.duration_minutes} minutes`] as [string, string]] : []),
              ...(exam.question_count ? [['Questions', String(exam.question_count)] as [string, string]] : []),
              ...(exam.pass_mark !== null ? [['Pass mark', `${exam.pass_mark}%`] as [string, string]] : []),
            ]} /></Panel>
            {!!exam.subjects?.length && <Panel><p className="text-lg font-bold text-[#0A2540]">Subjects</p><p className="text-slate-600 mt-1">{exam.subjects.map((x) => x.name ?? 'Subject').join(', ')}</p></Panel>}
            <Panel><p className="text-lg font-bold text-[#0A2540]">{exam.mode === 'cbt' ? 'Before you start' : 'Bring'}</p>
              <p className="text-slate-600 mt-1">{exam.instructions || (exam.mode === 'cbt' ? 'Use a charged phone or computer with good internet. The timer keeps running once you start, even if you close the page. Answers save as you go.' : 'Pen, pencil, ruler and your application reference. Arrive 30 minutes early.')}</p></Panel>
            {exam.mode === 'cbt' && !exam.can_start && exam.status === 'scheduled' && <p className="text-center text-sm text-slate-500">The exam opens at the time above.</p>}
          </>
        )}
        <Actions>
          {exam?.mode === 'cbt' && exam.can_start ? (
            <Primary busy={start.isPending} onClick={async () => { if (await askConfirm('The timer starts immediately.', { title: 'Start the entrance exam now?', confirmLabel: 'Start exam' })) start.mutate(); }}>Take entrance exam</Primary>
          ) : exam && (exam.status === 'graded' || exam.status === 'taken') ? (
            <Primary to="/applicant/result">See my result</Primary>
          ) : (
            <Primary to="/applicant/overview">Back to tracker</Primary>
          )}
        </Actions>
      </Body>
    </Screen>
  );
}
